/**
 * The landing page's stylesheet, injected once on mount and removed on
 * teardown — the same pattern `base-view/ui/styles.ts` uses, and for the same
 * reason: this page owns a lot of chrome that must not leak into the game
 * screens the SPA swaps to.
 *
 * ─── It follows the game's style guide, not a marketing template ────────────
 * `design/gdd/ui-visual-style.md` is explicit and this page is bound by it:
 *
 *   - **No minimalism.** No flat fills, no undecorated one-pixel borders, no
 *     generic dashboard chrome. That rules out the obvious landing-page look.
 *   - **Every surface is a material.** Panels are steel, paper or canvas.
 *   - **Light falls unevenly.** A two-stop gradient is the FLOOR, not the
 *     target — the guide records that the first pass treated it as sufficient
 *     and it still read as flat. Every surface here layers a radial highlight
 *     or a third stop on top.
 *   - **Borders are built.** Corner rivets and taped corners, not `border:
 *     1px solid` on its own. This used to say "clipped corners" too, which
 *     the GDD rule never did — and since 2026-09-18 the cut corner is chrome
 *     only (header, drawer, icon buttons; see the rule near `.lp-icon-btn`),
 *     so a section or a call to action must build its frame some other way.
 *   - **Typography carries identity.** Three faces with three jobs: stencil
 *     display for chrome and headings, Special Elite for in-world written
 *     text, Share Tech for technical readouts and numbers.
 *
 * ─── Tokens come from `src/styles/app.css` ──────────────────────────────────
 * `--brass`, `--bone`, `--rust` and the rest are declared there, in a plain
 * global stylesheet `index.html` loads and nothing ever unmounts, so they are
 * available here whatever this module does. Worth stating because the sign-out
 * chrome in `bootstrap.ts` carries a long-lived comment claiming the opposite
 * — that one is corrected in place, and this file is the tokenised one.
 *
 * ─── The CSS is a template literal ──────────────────────────────────────────
 * A backtick anywhere in here — including inside a comment — terminates the
 * string, and esbuild reports the failure dozens of lines away from the cause.
 * Use apostrophes.
 */

const STYLE_ID = 'tg-landing-styles';

const CSS = `
/* ── The backdrop ────────────────────────────────────────────────────────────
   The live settlement is a fixed layer behind #app, mounted into <body> by
   landing-backdrop.ts, which also carries its CSS — the layer appears on auth
   routes that never inject THIS sheet, and a layer with no box sizes its
   renderer to a one-pixel canvas.

   What belongs here is only what the landing page itself does differently
   while that layer is up. */
body.lp-backdrop-live .lp { background: none; }
body.lp-backdrop-live .lp-hero-scene { background: transparent; }
body.lp-backdrop-live .lp-hero-poster { display: none; }

/* ── Page shell ──────────────────────────────────────────────────────────── */
.lp {
  --lp-header-h: 66px;
  /* Janeks: *"Full width container max width -> 1600px"*. One token, so the
     header, the hero copy, every section, the footer and the colophon cannot
     disagree — they each used to carry their own 1180. */
  --lp-max: 1600px;
  /* The diagonal cut's rise, left edge to right edge. Declared on .lp rather
     than on .lp-hero because FOUR rules have to agree on it exactly — the
     hero's clip, the hazard band's clip, the copy's bottom padding, and the
     first section below, whose scrim follows the same line. A custom property
     on the hero is invisible to its siblings. */
  --lp-slant: 5vw;
  min-height: 100dvh;
  color: var(--bone);
  font-family: var(--font-technical);
  background:
    radial-gradient(120% 60% at 50% 0%, rgba(90, 54, 22, 0.28) 0%, rgba(0, 0, 0, 0) 60%),
    linear-gradient(180deg, var(--page-b) 0%, var(--page-a) 40%, var(--ground) 100%);
  overflow-x: hidden;
}
.lp * { box-sizing: border-box; }
/* The header menu is a set of in-page anchors, so the jump should read as
   travel rather than as a cut. Set on the ROOT, because the element that
   scrolls is the document, not .lp. Turned off again under reduced motion. */
html:has(.lp) { scroll-behavior: smooth; }
.lp ::selection { background: rgba(171, 138, 83, 0.35); }

/* Grain. One repeating conic sliver, rotated, at very low alpha — cheaper than
   a noise image and it does the job the guide asks of every large surface. */
.lp-grain {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 3;
  opacity: 0.05;
  background-image:
    repeating-linear-gradient(37deg, rgba(255, 255, 255, 0.5) 0 1px, rgba(0, 0, 0, 0) 1px 3px),
    repeating-linear-gradient(-51deg, rgba(0, 0, 0, 0.6) 0 1px, rgba(0, 0, 0, 0) 1px 4px);
  mix-blend-mode: overlay;
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.lp-header {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 40;
  height: var(--lp-header-h);
  display: flex;
  align-items: center;
  gap: 18px;
  /* Centres the bar's CONTENT on the same measure as the sections below, while
     the bar itself stays full-bleed. Without it the logo drifts away from the
     copy's left edge as the viewport passes 1600. */
  padding: 0 max(clamp(14px, 3vw, 34px), calc((100vw - var(--lp-max)) / 2 + clamp(18px, 4vw, 40px)));
  color: var(--bone);
  /* Four stops, not two: a straight 0.92 to 0 fade (or the old 0.92/0.55/0
     three-stop) holds most of its opacity through the middle, then drops the
     rest in the last ~20px — against a busy hero photo that steep a cutoff
     reads as a hard edge, the "strange bottom border" it looked like. Easing
     the last stretch out over more of the bar's height removes the seam
     without giving up the top's near-solid wash the nav needs to stay legible. */
  background: linear-gradient(180deg,
    rgba(10, 7, 4, 0.92) 0%, rgba(10, 7, 4, 0.6) 45%, rgba(10, 7, 4, 0.2) 80%, rgba(10, 7, 4, 0) 100%);
  transition: background 0.25s ease, box-shadow 0.25s ease;
  /* No border at rest. A transparent 1px border is still part of the border
     box the background paints into, so the gradient repeats there and draws a
     dark hairline across the hero (it cut through the motto on a phone). */
}
/* Set once the page has scrolled past the hero fold: the bar becomes a real
   plate instead of a gradient wash, so the nav stays readable over copy. */
.lp-header[data-stuck='true'] {
  background:
    repeating-linear-gradient(112deg, rgba(0, 0, 0, 0.22) 0 2px, rgba(0, 0, 0, 0) 2px 13px),
    linear-gradient(180deg, #221a11 0%, #130d07 100%);
  border-bottom: 1px solid var(--line);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.55), inset 0 -1px 0 rgba(255, 255, 255, 0.05);
}

.lp-brand {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  flex: none;
  text-decoration: none;
  color: inherit;
}
.lp-brand:hover { text-decoration: none; }
/* The illustrated wordmark — see brandLogo() in landing-chrome.ts. It
   replaced a gas-mask SVG mark plus separate name/settlement text, since the
   badge already carries both in the art; a fixed height keeps the header from
   growing to fit it, width is auto so the 2.29:1 art doesn't distort. */
.lp-brand-logo { display: block; height: 34px; width: auto; flex: none; }

.lp-nav { display: flex; align-items: center; gap: 4px; margin-left: auto; }
.lp-nav a {
  position: relative;
  padding: 9px 12px;
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--bone-dim);
  text-decoration: none;
  transition: color 0.14s ease;
}
.lp-nav a::after {
  content: '';
  position: absolute;
  left: 12px; right: 12px; bottom: 4px;
  height: 2px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, var(--brass-hot) 50%, rgba(0, 0, 0, 0) 100%);
  transform: scaleX(0);
  transition: transform 0.18s ease;
}
.lp-nav a:hover { color: var(--bone); text-decoration: none; }
.lp-nav a:hover::after { transform: scaleX(1); }

.lp-header-actions { display: flex; align-items: center; gap: 9px; flex: none; }

/* ── Buttons ─────────────────────────────────────────────────────────────── */
/* Clipped corners rather than a radius: the guide asks for frames that read as
   cut and joined. The sign-out chrome in bootstrap.ts uses the same silhouette,
   so the two agree across the auth boundary. */
.lp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 20px;
  border: 1px solid var(--line);
  color: var(--bone);
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  background:
    radial-gradient(120% 140% at 22% 0%, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 55%),
    repeating-linear-gradient(112deg, rgba(0, 0, 0, 0.2) 0 2px, rgba(0, 0, 0, 0) 2px 12px),
    linear-gradient(180deg, #2c2116 0%, #1a1208 60%, #120c06 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.6), 0 6px 16px rgba(0, 0, 0, 0.45);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
  transition: color 0.14s ease, border-color 0.14s ease, filter 0.14s ease, transform 0.14s ease;
}
.lp-btn:hover { filter: brightness(1.22); text-decoration: none; transform: translateY(-1px); }
.lp-btn:active { transform: translateY(0); }
.lp-btn svg { width: 16px; height: 16px; display: block; flex: none; }

/* The one call to action on the page that matters. Brass, lit, and the only
   thing wearing a glow. */
.lp-btn-primary {
  color: #1a0f05;
  border-color: #6a4c1e;
  background:
    radial-gradient(130% 160% at 26% 0%, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0) 52%),
    linear-gradient(180deg, var(--brass-hot) 0%, var(--brass-bright) 45%, #8a6a33 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55), inset 0 -3px 8px rgba(70, 40, 8, 0.6),
    0 8px 22px rgba(0, 0, 0, 0.5), 0 0 22px rgba(220, 176, 106, 0.18);
  text-shadow: 0 1px 0 rgba(255, 233, 190, 0.5);
}
.lp-btn-primary:hover { filter: brightness(1.1); }
.lp-btn-lg { padding: 15px 30px; font-size: 16px; }

/* Square icon button — mute, burger. */
.lp-icon-btn {
  width: 42px;
  padding: 0;
  color: var(--brass);
}
.lp-icon-btn:hover { color: var(--brass-hot); }
.lp-icon-btn svg { width: 19px; height: 19px; }

/* The cut corner, and the only place left on this page that has one.
   Janeks: *"ditch the cut corner style on sections and main buttons (can be
   left on navigation buttons and small buttons)."* The header and the drawer
   ARE the navigation and the icon buttons are the small ones, so the notch
   stays on those three; the hero's two calls to action, the closing section's
   pair and every card and sheet on the page lost it.

   Scoped by ancestor rather than baked into .lp-btn because one class does
   both jobs: "lp-btn lp-btn-primary" is the header's Play button AND the
   hero's. (No backticks in here — this stylesheet is a template literal and
   one would end the string; esbuild then blames a line 40 rows down.) */
.lp-header .lp-btn,
.lp-drawer .lp-btn,
.lp-icon-btn {
  clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px));
}

/* ── Mobile drawer ───────────────────────────────────────────────────────── */
/* The header's Play button carries two labels and shows one. A phone header
   has to fit a mark, a mute toggle, a call to action and a burger across
   390 px; "Play free" is what makes it overflow, and CSS cannot shorten text. */
.lp-label-narrow { display: none; }
.lp-burger { display: none; }
.lp-drawer {
  position: fixed;
  inset: var(--lp-header-h) 0 auto 0;
  z-index: 39;
  display: none;
  flex-direction: column;
  padding: 10px 16px 20px;
  background:
    repeating-linear-gradient(112deg, rgba(0, 0, 0, 0.24) 0 2px, rgba(0, 0, 0, 0) 2px 13px),
    linear-gradient(180deg, #221a11 0%, #100b06 100%);
  border-bottom: 1px solid var(--line);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.65);
}
.lp-drawer[data-open='true'] { display: flex; }
.lp-drawer a {
  padding: 13px 6px;
  border-bottom: 1px dashed rgba(60, 48, 31, 0.8);
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--bone);
  text-decoration: none;
}
.lp-drawer a:last-of-type { border-bottom: 0; }

/* ── Hero ────────────────────────────────────────────────────────────────── */
/* Janeks: *"Add a max height, so that we have some safe-space in case a giant
   monitor is opened."* Without the cap the hero is 100dvh whatever that is, so
   a tall display pushes the first content block a screen and a half down and
   the page looks like it has nothing on it. 1120 is comfortably over a 16:9
   laptop's height, so the cap only bites on the displays that were the
   problem — raised from 960 on Janeks' note that the hero *"can be slightly
   larger vertically"*. */
.lp-hero {
  position: relative;
  min-height: min(100dvh, 1120px);
  max-height: 1120px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  /* Janeks: *"Make the hazard line diagonal itself masking the hero."* So the
     hero's bottom edge IS the diagonal — the section is cut and the page's own
     background shows through the triangle underneath, rather than a straight
     band laid across a straight edge. */
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - var(--lp-slant)), 0 100%);
}
/* Holds the WebGL canvas (or the poster, when the scene is not run). */
.lp-hero-scene { position: absolute; inset: 0; z-index: 0; background: #0b0805; }
.lp-hero-scene canvas { display: block; width: 100%; height: 100%; }
/* The still shown wherever the live scene is not run: a phone, a visitor who
   asked for reduced motion, a browser with no WebGL, a chunk that failed to
   load. The poster file and the gradient are layers of ONE background-image on
   purpose — if the file is missing the image layer simply does not paint and
   the gradient underneath is what shows, so the hero degrades to a lit
   twilight rather than to black. Anything that overrides background-image
   here (an inline style, say) takes the gradient down with the poster. */
.lp-hero-poster {
  position: absolute;
  inset: 0;
  background-image:
    url('/landing/hero-poster.webp'),
    radial-gradient(48% 34% at 34% 62%, rgba(255, 150, 60, 0.4) 0%, rgba(255, 120, 40, 0) 68%),
    linear-gradient(180deg, #070510 0%, #3a2414 62%, #120d09 100%);
  background-size: cover, auto, auto;
  background-position: 50% 58%, center, center;
  background-repeat: no-repeat;
}
/* Two veils, not one. The bottom one carries the copy; the top keeps the
   header legible against a bright patch of sky. */
.lp-hero-veil {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(8, 5, 3, 0.75) 0%, rgba(8, 5, 3, 0) 26%),
    linear-gradient(0deg, rgba(8, 5, 3, 0.9) 0%, rgba(8, 5, 3, 0.62) 30%, rgba(8, 5, 3, 0) 64%);
}
/* The hazard band, running ALONG the diagonal cut rather than across the
   bottom of it.
   The style guide reserves the .hazard-band class for "blocked/dangerous state
   ONLY — never a category key or decoration", so this does not reuse it: it is
   the same diagonal language in the landing page's own colours, marking where
   the settlement ends and the rest of the page begins.
   The clip is a parallelogram whose two long edges are both parallel to the
   hero's cut, offset by the band's thickness — which is why both rules read
   var(--lp-slant) off the same element. */
.lp-hero::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  background: repeating-linear-gradient(
    -48deg,
    var(--haz-a) 0 18px,
    var(--haz-b) 18px 36px);
  opacity: 0.85;
  filter: drop-shadow(0 -7px 16px rgba(0, 0, 0, 0.8));
  clip-path: polygon(
    0 calc(100% - 17px),
    100% calc(100% - var(--lp-slant) - 17px),
    100% calc(100% - var(--lp-slant)),
    0 100%);
}

.lp-hero-copy {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: var(--lp-max);
  margin: 0 auto;
  /* The bottom padding clears the SLOPE as well as the usual margin, or the
     last line of the pitch sits under the band on a wide screen, where the
     rise is largest. */
  padding: 0 clamp(18px, 4vw, 40px) calc(clamp(48px, 9vh, 96px) + var(--lp-slant));
}
.lp-motto {
  display: inline-block;
  margin: 0 0 18px;
  /* The side padding clears the torn ends of the tape picture. */
  padding: 11px 28px 10px;
  font-family: var(--font-note);
  font-size: clamp(13px, 1.5vw, 16px);
  letter-spacing: 0.08em;
  color: #3b2d17;
  /* Generated tape strip (Higgsfield), blank: the words stay live text on it.
     Its torn ends are part of the picture, so no clip-path here. */
  background: url('/clutter/motto-tape.webp') center / 100% 100% no-repeat;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.55));
  transform: rotate(-1.1deg);
}
/* The illustrated wordmark, replacing the stencil-text title. Janeks: *"Add
   this file as logo ... put in everywhere we have the logo needed."* Width
   is what actually scales it — clamp() on a percentage-of-viewport basis,
   the same idea the text size used, with height following from the image's
   own 2.3:1 aspect ratio so nothing distorts. A drop-shadow filter (not a
   text-shadow, which does nothing on an img) keeps the same lift off the
   hero background the text had. */
.lp-title {
  margin: 0;
  line-height: 0;
}
.lp-title img {
  display: block;
  width: clamp(280px, 46vw, 780px);
  height: auto;
  filter: drop-shadow(0 14px 30px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 42px rgba(255, 160, 70, 0.18));
}
.lp-pitch {
  max-width: 44ch;
  margin: 22px 0 30px;
  font-family: var(--font-technical);
  font-size: clamp(15px, 1.75vw, 19px);
  line-height: 1.6;
  color: var(--bone-dim);
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.9);
}
.lp-hero-actions { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }

/* ── Section scaffolding ─────────────────────────────────────────────────── */
/* ── The scrim behind everything below the hero ──────────────────────────────
   With a live backdrop the page has no opaque background of its own, so the
   copy needs something to be readable against — dense on the left where the
   text is, clearing toward the right so the settlement still shows through.

   ONE box for the whole run, not one per section. Janeks: *"make sure all
   blocks are side to side and dont bleed any unmasked background."* Per-section
   scrims tiled with a 1 px overlap and a 22 px seam still opened under the
   hero: a strip of raw settlement straight across the page. A single element
   cannot have a seam in it, so the class of bug is gone rather than patched.

   Full VIEWPORT width, not the container's: the blocks are capped at
   --lp-max and centred, so a scrim inset to them left two bright bands of
   settlement down the page on anything wider than 1600. The 50%/-50vw pair is
   the standard break-out.

   Its TOP EDGE FOLLOWS THE HERO'S CUT. Janeks: *"the first block under
   diagonal line should follow the geometry."* The hero's lower edge runs from
   its bottom-left corner up to --lp-slant above its bottom-right, so this is
   raised by that much and clipped along the same line; the two edges meet and
   the whole thing reads as one continuous diagonal instead of a slope followed
   by a straight rule. */
.lp-blocks { position: relative; }
body.lp-backdrop-live .lp-blocks::before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--lp-slant));
  bottom: -1px;
  left: 50%;
  width: 100vw;
  margin-left: -50vw;
  z-index: -1;
  background: linear-gradient(90deg,
    rgba(10, 7, 4, 0.94) 0%,
    rgba(10, 7, 4, 0.9) 48%,
    rgba(10, 7, 4, 0.72) 78%,
    rgba(10, 7, 4, 0.42) 100%);
  clip-path: polygon(0 var(--lp-slant), 100% 0, 100% 100%, 0 100%);
}
body.lp-backdrop-live .lp-footer { background: rgba(8, 5, 3, 0.93); }

/* ── Keeping the copy out of the fade ────────────────────────────────────────
   Janeks: *"make the text containers shorter, add a large padding on the right
   side, so that content is not influenced by the gradient opacity transition
   that hard."*

   The scrim behind the blocks clears from 78% rightward so the settlement
   shows through, and the section's right-edge mask fades from 62%. Copy that
   ran the full measure therefore had its last words sitting in the part of the
   page that is deliberately going transparent — legible in a screenshot,
   marginal over a moving scene.

   So the text gets a right gutter rather than the gradients getting moved:
   the fade is doing its job and the copy simply should not be in it. Two
   separate limits, because they answer different questions — the padding
   keeps the BLOCK clear of the fade, the max-width keeps a LINE readable at
   1600, and a line length that happens to fall out of a percentage is not a
   measure. */
.lp-section {
  position: relative;
  z-index: 2;
  max-width: var(--lp-max);
  margin: 0 auto;
  padding: clamp(64px, 11vh, 118px) clamp(18px, 4vw, 40px);
  scroll-margin-top: var(--lp-header-h);
}
.lp-section + .lp-section { border-top: 1px solid rgba(60, 48, 31, 0.55); }
.lp-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  font-family: var(--font-technical);
  font-size: 11px;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: var(--brass);
}
.lp-eyebrow::before {
  content: '';
  width: 26px;
  height: 2px;
  background: linear-gradient(90deg, var(--brass-hot), rgba(0, 0, 0, 0));
}
.lp-h2 {
  margin: 0 0 18px;
  max-width: 24ch;
  font-family: var(--font-display);
  font-size: clamp(30px, 4.4vw, 50px);
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: 0.005em;
  color: var(--bone);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.7);
}
.lp-body {
  max-width: 58ch;
  margin: 0 0 26px;
  font-size: clamp(15px, 1.6vw, 17.5px);
  line-height: 1.68;
  color: var(--bone-dim);
}
/* The section stacks rather than splitting into columns.
   An earlier pass ran heading+body+points down a wide left column with the
   three stat plates alone in a narrow right one; the plates are ~130 px tall
   against a ~660 px column, so two thirds of the section was empty. Full-width
   stats between the prose and the list fill the measure and give the eye a
   break between two blocks of text. */

/* ── Point list — stamped tags, compact ──────────────────────────────────────
   Janeks, twice. First: *"restyle the list items. Make them Thematic cards /
   metal items or something like that, dont use the plain WEB style conventions
   on this page as a rule."* Then, on what that produced: *".lp-points -
   redesign these, they are too repetitive and large."*

   Both notes are right and they pull in opposite directions, so the answer is
   not a third surface — it is LESS surface. The previous version gave each
   point a full brushed-steel plate with a rivet, 19 px of padding and a 46 px
   gutter; four of those stacked read as four identical machine parts and ate
   half the section. These keep the stamped-metal identity and drop almost all
   of the weight:

     - no plate. The tag is a thin dark strip with a brass edge, not a panel
     - the index is a small stamped chip at the left, not a gutter
     - the corner cut ALTERNATES side down the list, so four of them in a row
       do not tile into one grey block
     - 13.5 px text, 11 px padding — about half the height each

   The material still reads at a glance; it just stops shouting. */
.lp-points {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 7px;
  counter-reset: lp-plate;
}
@media (min-width: 900px) { .lp-points { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 12px; } }

.lp-points li {
  counter-increment: lp-plate;
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 11px;
  padding: 10px 13px 10px 11px;
  font-family: var(--font-technical);
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: 0.01em;
  color: #e6dcc7;
  background:
    linear-gradient(176deg, rgba(90, 88, 82, 0.5) 0%, rgba(38, 37, 33, 0.72) 100%);
  border: 1px solid rgba(20, 16, 10, 0.9);
  border-left: 2px solid var(--brass);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.09), 0 3px 9px rgba(0, 0, 0, 0.35);
  transition: background 0.16s ease, border-color 0.16s ease;
}
/* Alternating cut corner. The one thing that stops a column of these reading
   as a single repeated element. */
.lp-points li:nth-child(odd) {
  clip-path: polygon(0 0, calc(100% - 11px) 0, 100% 11px, 100% 100%, 0 100%);
}
.lp-points li:nth-child(even) {
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - 11px), calc(100% - 11px) 100%, 0 100%);
}
.lp-points li:hover {
  background: linear-gradient(176deg, rgba(104, 101, 94, 0.6) 0%, rgba(46, 45, 40, 0.78) 100%);
  border-left-color: var(--brass-hot);
}

/* The stamped index — struck into a small chip rather than printed on a plate.
   The light bevel under the dark fill is the whole trick; without it the digits
   read as grey text. */
.lp-points li::before {
  content: counter(lp-plate, decimal-leading-zero);
  font-family: var(--font-display-stencil);
  font-size: 15px;
  font-weight: 800;
  line-height: 1.5;
  /* Brass, not a black depression. On the old full-size plate the strip was
     light enough for a dark stamp to read; on this thin dark tag it vanished
     into the background, which is the same "struck metal needs something to be
     struck into" problem from the other direction. */
  color: var(--brass);
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.8);
}

/* ── Stat rows — three treatments, cycled down the page ──────────────────────
   Janeks: *"change the view of .lp-stat (3 different styles - current but
   better, Tapes from game styles, Naked (empty))"*. Which section wears which
   is decided in landing-copy.ts, not here, so the copy and the treatment
   travel together. The shared geometry lives on .lp-stats / .lp-stat; each
   .lp-stats-* modifier supplies the material. */
.lp-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0 0 26px;
}
.lp-stat {
  position: relative;
  padding: 17px 14px 15px;
  text-align: center;
}
.lp-stat-value {
  display: block;
  font-family: var(--font-display-stencil);
  font-size: clamp(30px, 4.4vw, 44px);
  font-weight: 800;
  line-height: 1;
  color: var(--brass-hot);
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.75), 0 0 18px rgba(255, 189, 92, 0.25);
}
.lp-stat-label {
  display: block;
  margin-top: 7px;
  font-family: var(--font-technical);
  font-size: 10.5px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #b9b4a6;
}

/* ── 1. PLATE — riveted steel. The original, with the light actually falling
   on it: a radial highlight, a brighter top stop and a real bevel, none of
   which the first version had enough of to read as metal. */
.lp-stats-plate .lp-stat {
  background:
    radial-gradient(130% 100% at 26% -10%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 55%),
    repeating-linear-gradient(90deg,
      rgba(255, 255, 255, 0.05) 0 1px, rgba(0, 0, 0, 0.08) 1px 2px, rgba(255, 255, 255, 0.018) 2px 4px),
    linear-gradient(180deg, #62615d 0%, #444340 42%, #2c2b27 100%);
  border: 1px solid #100c07;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -4px 9px rgba(0, 0, 0, 0.55),
    0 7px 18px rgba(0, 0, 0, 0.45);
}
.lp-stats-plate .lp-stat::before, .lp-stats-plate .lp-stat::after {
  content: '';
  position: absolute;
  top: 7px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, var(--brass-bright), var(--brass) 55%, #4a3a20 100%);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.6), inset 0 0 1px rgba(255, 255, 255, 0.4);
}
.lp-stats-plate .lp-stat::before { left: 7px; }
.lp-stats-plate .lp-stat::after { right: 7px; }

/* ── 2. TAPE — the game's own tape, with the number marked on it in sharpie.
   Janeks: *"make it realistic, not just a wiggly border. Can You use the image
   i supplied? just without the background? As if text on it is written by
   sharpie."*

   The images are already cut out: all four public/textures/tape-*.webp are
   RGBA with a real torn silhouette and nothing behind it. There was never a
   background to remove — the first version put one there. What it got wrong,
   twice over:

     - background-size: cover on a tile of a different aspect than the image
       scaled one several times over and showed a crop of its middle, so the
       torn ends were outside the box entirely;
     - a solid background-color and a clip-path underneath, which replaced
       the photograph's own edge with a CSS zig-zag — the "wiggly border".

   Now each tile takes its texture at contain, carries that texture's own
   aspect ratio so nothing distorts, and has no fill, no border and no clip.
   The alpha IS the edge. The number goes on top in Permanent Marker, tilted a
   degree or so, because a figure written on tape is written by hand. */
.lp-stats-tape {
  gap: clamp(10px, 1.6vw, 26px);
  grid-template-columns: repeat(3, minmax(0, 250px));
  justify-content: start;
  align-items: center;
}
.lp-stats-tape .lp-stat {
  /* The three textures are 2.0-2.35:1; one ratio for all three letterboxes
     the odd one by a few pixels of transparency, which is invisible, and
     stretching them instead would be obvious. */
  aspect-ratio: 2.25 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 14% 4%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  filter: drop-shadow(0 5px 10px rgba(0, 0, 0, 0.55));
}
.lp-stats-tape .lp-stat:nth-child(1) {
  background-image: url('/textures/tape-white-paper.webp');
  transform: rotate(-1.4deg);
}
.lp-stats-tape .lp-stat:nth-child(2) {
  background-image: url('/textures/tape-duct-grey.webp');
  transform: rotate(0.9deg);
}
.lp-stats-tape .lp-stat:nth-child(3) {
  background-image: url('/textures/tape-blue-isolation.webp');
  transform: rotate(-0.6deg);
}
/* Sharpie. Permanent Marker is already loaded for the game's own UI, so this
   costs nothing new. The slight counter-rotation is what keeps it reading as
   written ON the tape rather than composited over it. */
.lp-stats-tape .lp-stat-value {
  font-family: "Permanent Marker", "Segoe Print", cursive;
  font-size: clamp(26px, 3.2vw, 38px);
  font-weight: 400;
  line-height: 1;
  color: #1a1512;
  text-shadow: none;
  transform: rotate(-1.1deg);
}
.lp-stats-tape .lp-stat-label {
  font-family: "Permanent Marker", "Segoe Print", cursive;
  font-size: clamp(8.5px, 0.85vw, 11px);
  letter-spacing: 0.08em;
  margin-top: 4px;
  color: #3a322c;
  transform: rotate(-1.1deg);
}

/* ── 3. NAKED — no surface at all, just the number and its label.
   The row that gives the eye somewhere to rest on a page where everything else
   is a material.

   Janeks: *"naked values need proper positioning"*. What was wrong: the tiles
   were centred text in a plain grid with a left border and ad-hoc padding, so
   the three numbers sat at three different optical left edges and the first
   did not line up with the heading above it. Now every tile is a column
   aligned to its own left edge, the tiles share a baseline by growing from the
   bottom, and the first is flush with the section's measure. */
.lp-stats-naked {
  gap: 0;
  align-items: stretch;
  margin-bottom: 30px;
}
.lp-stats-naked .lp-stat {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-top: 18px;
  padding: 2px 16px 2px 26px;
  text-align: left;
  border-left: 1px solid rgba(60, 48, 31, 0.8);
}
/* Flush left, so the number's stem lines up with the heading and the body. */
.lp-stats-naked .lp-stat:first-child { padding-left: 0; border-left: 0; }
.lp-stats-naked .lp-stat-value {
  color: var(--bone);
  text-shadow: none;
  font-size: clamp(38px, 5vw, 56px);
  /* The stencil face carries side bearing that holds the glyph in from its
     box edge. Pulling it back is what actually lines the digit up with the
     text above it, rather than merely putting its BOX in the right place. */
  margin-left: -0.045em;
}
.lp-stats-naked .lp-stat-label {
  color: var(--brass);
  margin-top: 7px;
  letter-spacing: 0.2em;
}

/* ── A section with a 3D preview beside it ───────────────────────────────── */
.lp-section-split { display: grid; gap: clamp(24px, 4vw, 54px); grid-template-columns: 1fr; }
/* The gutter. Sized against the same 62%/78% the mask and the scrim use, so
   the three move together if any of them is retuned. Only from 1000px up —
   below that there is no backdrop, no mask and no room to give away. */
@media (min-width: 1000px) {
  .lp-section { padding-right: clamp(60px, 16vw, 340px); }
  .lp-cta { padding-right: clamp(18px, 4vw, 40px); }
  /* A section with a preview beside it is already narrowed by its own column,
     and the preview must keep the full width it was given. */
  .lp-has-preview { padding-right: clamp(18px, 4vw, 40px); }
  .lp-section-split { grid-template-columns: minmax(0, 1fr) minmax(300px, 420px); align-items: start; }
  /* The weapon gets twice the column. Janeks: *"make the weapon part 2x wider,
     but dont change the models scales. just the viewport is wider."* A rifle
     is a long horizontal object on a turntable, so its sweep needs room the
     upright survivor does not.

     The second half of that instruction is enforced in landing-preview.ts,
     NOT here: the weapon's frame is measured in METRES OF WORLD, which this
     rule does not touch, so widening the column gives the model more room to
     turn in without making it any bigger.

     SHARE OF THE ROW, not a flat 840px. A track whose max is a length is not
     flexible, so grid gave it all 840 pixels before the 1fr beside it saw
     any — and every laptop narrower than about 1500px got the copy in
     whatever was left. At 1000px that was FORTY PIXELS: one word per line,
     a section 3700px tall, and a weapon fitted to the height of it. That is
     the "gigantic" Janeks was looking at. 55% keeps the doubled column at
     every width that can afford it (1520px of content and up hits the 840
     cap) and gives it back when it cannot. */
  .lp-has-preview-weapon .lp-section-split {
    grid-template-columns: minmax(0, 1fr) minmax(300px, min(840px, 55%));
  }
  /* The plates go single-column beside a preview — two columns of text in a
     narrowed measure wrap to three lines each and stop being scannable. */
  .lp-has-preview .lp-points { grid-template-columns: minmax(0, 1fr); }
}
/* No frame, no well, no backdrop.
   Janeks: *"dont open the 3d models in a separate container, load them in as
   3D models in front of the background of the page itself."* So this element
   is pure geometry — it reserves the column and nothing else. The canvas
   inside it is transparent and does not take pointer events, so the page's own
   gradient runs behind the model and the copy beside it stays clickable. */
/* Weapon and survivor both use this box, inside .lp-section-split — Janeks:
   *"[the weapon block] needs to be in 2 columns. 1 column text and data,
   other - 3D weapon model. Same as the character block."* A dedicated
   .lp-weapon-row/.lp-preview-wide once gave the weapon a full-width strip
   under the copy instead; that's gone, so there is only one preview layout
   to keep working now, not two. min-height, not height: the column's real
   height comes from .lp-section-copy's content (align-self: stretch below
   matches it), and a weapon needs at least as much room as a survivor to
   fit a hockey stick's length. */
.lp-preview {
  position: relative;
  min-height: 420px;
  align-self: stretch;
  /* Belt and braces: whatever the framing math in landing-preview.ts computes,
     the canvas itself can never paint past this box's edge. */
  overflow: hidden;
}
/* The weapon frame is SQUARE, and that is a declaration rather than a
   coincidence. WEAPON_VIEW_METRES makes the model's apparent size a share of
   this box's HEIGHT, and a stretched box takes its height from the copy beside
   it — so a long section or a narrow window grew the weapon by growing the
   text next to it, which is most of how it got out of hand (at 1280px the box
   was 1228 tall and the model was fitted to that).

   Without the aspect-ratio the canvas and its host chase each other to the
   same answer anyway: the renderer sizes the canvas from the host, and an
   auto-height host then takes ITS height from the canvas's intrinsic ratio.
   That loop happens to converge on a square, which is not a thing to leave
   holding up a layout. Centred, so the rest of the row's height stays as air
   above and below. */
.lp-has-preview-weapon .lp-preview {
  aspect-ratio: 1;
  height: auto;
  max-height: 840px;
  align-self: center;
  /* The 420px floor above would out-vote the ratio on a box narrower than
     that, which cannot happen here — the track's own 300px floor only binds
     under a ~545px container and this whole block is behind a 1000px media
     query — so it is cleared rather than left sitting there looking
     load-bearing. */
  min-height: 0;
}
.lp-preview canvas { display: block; width: 100%; height: 100%; }
/* The model is NOT captioned on screen. Janeks: *"under 3D models - dont show
   the labels - .lp-preview-label"*. The element is still built — it names the
   model for a screen reader, and the probe scripts read it to know which body
   or weapon was drawn — so it is hidden the accessible way rather than
   deleted. */
.lp-preview-label {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.lp-preview:empty { display: none; }
@media (max-width: 999px) { .lp-preview { min-height: 0; display: none; } }

/* ── Closing call to action ──────────────────────────────────────────────── */
.lp-cta {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: clamp(60px, 10vh, 110px) clamp(18px, 4vw, 40px);
  border-top: 1px solid rgba(60, 48, 31, 0.55);
  background:
    radial-gradient(70% 120% at 50% 100%, rgba(150, 82, 26, 0.22) 0%, rgba(0, 0, 0, 0) 68%);
}
.lp-cta h2 {
  margin: 0 0 10px;
  font-family: var(--font-display-stencil);
  font-size: clamp(28px, 5vw, 54px);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--bone);
}
.lp-cta p {
  margin: 0 auto 26px;
  max-width: 52ch;
  font-family: var(--font-note);
  font-size: clamp(14px, 1.6vw, 17px);
  line-height: 1.6;
  color: var(--bone-dim);
}
.lp-cta .lp-hero-actions { justify-content: center; }

/* ── Footer ──────────────────────────────────────────────────────────────── */
.lp-footer {
  position: relative;
  z-index: 2;
  padding: clamp(36px, 6vh, 60px) clamp(18px, 4vw, 40px) 30px;
  border-top: 1px solid var(--line);
  background: linear-gradient(180deg, rgba(8, 5, 3, 0.4) 0%, var(--ground) 100%);
  font-size: 13.5px;
  color: var(--bone-dim);
}
.lp-footer-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  display: grid;
  gap: 30px;
  grid-template-columns: 1fr;
}
@media (min-width: 760px) { .lp-footer-inner { grid-template-columns: 1.4fr 1fr 1fr; } }
.lp-footer h3 {
  margin: 0 0 12px;
  font-family: var(--font-technical);
  font-size: 11px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--brass);
}
.lp-footer-links { display: grid; gap: 9px; }
/* Two columns on desktop. Janeks: *"split the 'game' menu into 2 columns on
   desktops."* grid-auto-flow: column with an explicit row count fills DOWN
   the first column and then the second, which is how a menu reads; the default
   row flow would run it across and make the order look shuffled. */
@media (min-width: 760px) {
  .lp-footer-links-split {
    grid-auto-flow: column;
    grid-template-rows: repeat(3, auto);
    grid-auto-columns: minmax(0, 1fr);
    column-gap: 22px;
    justify-content: start;
  }
}
.lp-footer-links a { color: var(--bone-dim); text-decoration: none; width: fit-content; }
.lp-footer-links a:hover { color: var(--brass-hot); text-decoration: none; }
.lp-footer-blurb { max-width: 46ch; line-height: 1.65; font-family: var(--font-note); }
.lp-socials { display: flex; flex-wrap: wrap; gap: 9px; }
.lp-social {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border: 1px solid var(--line);
  color: var(--bone-dim);
  text-decoration: none;
  font-family: var(--font-technical);
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: linear-gradient(180deg, rgba(42, 33, 22, 0.8), rgba(16, 12, 8, 0.8));
  clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px));
}
.lp-social:hover { color: var(--brass-hot); text-decoration: none; }
.lp-colophon {
  max-width: var(--lp-max);
  margin: 34px auto 0;
  padding-top: 18px;
  border-top: 1px dashed rgba(60, 48, 31, 0.8);
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  align-items: center;
  justify-content: space-between;
  font-family: var(--font-technical);
  font-size: 11.5px;
  letter-spacing: 0.1em;
  color: var(--bone-faint);
}

/* ── The operator's registered details, under the footer logo ─────────────── */
.lp-company {
  display: grid;
  gap: 3px;
  margin: 14px 0 0;
  font-family: var(--font-technical);
  font-style: normal;
  font-size: 12.5px;
  line-height: 1.5;
  letter-spacing: 0.03em;
  color: var(--bone-faint);
  max-width: 40ch;
}
.lp-company span:first-child { color: var(--bone-dim); letter-spacing: 0.1em; text-transform: uppercase; }

/* ── Sub-pages (/news, /news/:slug, the legal pages) ──────────────────────────────────────────
   No full-bleed hero, so the content has to clear the fixed header itself. */
.lp-subpage main { padding-top: var(--lp-header-h); }
.lp-back {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  margin-bottom: 8px;
  font-family: var(--font-technical);
  font-size: 12px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--brass);
  text-decoration: none;
}
.lp-back:hover { color: var(--brass-hot); text-decoration: none; }

/* ── The news board ──────────────────────────────────────────────────────────
   Notices nailed up rather than a feed: each card is a sheet of aged paper on
   a board, held by a strip of tape across the top-left corner. Same reasoning
   as the point plates — no web-card conventions on this page. */
.lp-news-list { display: grid; gap: 16px; margin-top: 26px; }
@media (min-width: 820px) { .lp-news-list { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

.lp-news-card {
  position: relative;
  display: grid;
  /* start, NOT the grid default of stretch. Cards in a row are as tall as the
     tallest one, and a stretched grid blows every row up to fill the slack --
     which turns the tag chip into a big block and floats the title in the
     middle of the sheet. Invisible until a card carried a header image and
     the heights stopped matching. */
  align-content: start;
  gap: 7px;
  padding: 22px 22px 20px;
  text-decoration: none;
  color: #2a2118;
  background:
    radial-gradient(90% 70% at 18% 0%, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0) 58%),
    repeating-linear-gradient(101deg, rgba(120, 96, 52, 0.05) 0 2px, rgba(0, 0, 0, 0) 2px 9px),
    linear-gradient(170deg, var(--paper) 0%, #c2b087 100%);
  border: 1px solid #8d7c57;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}
.lp-news-card:hover {
  transform: translateY(-3px);
  text-decoration: none;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}
/* The tape across the corner. */
.lp-news-card::before {
  content: '';
  position: absolute;
  left: -18px;
  top: 12px;
  width: 78px;
  height: 21px;
  transform: rotate(-38deg);
  background: linear-gradient(174deg, rgba(195, 168, 116, 0.85), rgba(160, 134, 86, 0.85));
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  clip-path: polygon(3% 8%, 97% 0%, 100% 90%, 1% 100%);
}
.lp-news-tag {
  justify-self: start;
  padding: 3px 9px;
  font-family: var(--font-technical);
  font-size: 10px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #f3e7cc;
  background: linear-gradient(180deg, #4a3a20, #2b2012);
}
.lp-news-date {
  font-family: var(--font-technical);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #6c5c3c;
}
.lp-news-title {
  font-family: var(--font-display);
  font-size: clamp(20px, 2.4vw, 26px);
  font-weight: 800;
  line-height: 1.12;
  color: #1d1610;
}
.lp-news-summary { font-family: var(--font-note); font-size: 14.5px; line-height: 1.6; color: #43372a; }
.lp-news-more {
  margin-top: 4px;
  font-family: var(--font-technical);
  font-size: 11.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #6d4a1c;
}
.lp-news-empty {
  margin: 26px 0 0;
  font-family: var(--font-note);
  font-size: 15px;
  color: var(--bone-dim);
}

/* The header image on a card.
   Bled to the card's edges rather than inset, so the photo reads as part of
   the sheet instead of a framed thumbnail pasted onto it. A fixed 16/9 box
   with object-fit: cover, because the operator uploads whatever they have and
   three cards of different heights would break the row. */
/* The tape, over a photo.
   .lp-news-card::before is a semi-transparent strip, which is right on bare
   paper and wrong over an image -- at 0.85 alpha over a dark photo it stops
   reading as tape and becomes a dark notch cut out of the corner. Opaque, so
   it reads as a strip holding the picture down. Seen in a screenshot; no test
   could have caught it. */
.lp-news-card:has(.lp-news-thumb)::before {
  background: linear-gradient(174deg, #d9c091, #b79a68);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}
.lp-news-thumb {
  display: block;
  margin: -22px -22px 6px;
  /* A HEIGHT, not an aspect ratio.
     16/9 is a proportion, not a size: on the two-up board a card is ~640px
     wide, so the picture became a 360px band that dwarfed the words under
     it. Capping that with max-height does not work either -- aspect-ratio
     then shrinks the WIDTH to match, and the image stops reaching the card's
     edges (the negative margins depend on this box keeping its auto width).
     A definite height leaves the width alone and gives every card the same
     banner whatever it is standing in. */
  height: clamp(150px, 18vw, 210px);
  overflow: hidden;
  background: rgba(40, 31, 20, 0.35);
  border-bottom: 1px solid rgba(90, 76, 50, 0.6);
}
.lp-news-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Warmed and knocked back so a modern photograph sits on aged paper rather
     than on top of it. */
  filter: sepia(0.28) saturate(0.86) contrast(1.04) brightness(0.96);
}

/* Whats new -- the three latest dispatches on the landing page.
   Its own grid, NOT .lp-news-list: the board at /news is two columns from
   820px and this row is three, and overloading one class to mean both is how
   the two drift. One column below 900px, which is the brief -- on mobile the
   cards come one after another. */
.lp-news-home-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}
.lp-news-home-head .lp-body { margin-bottom: 0; max-width: 62ch; }
.lp-news-home-list {
  display: grid;
  gap: 16px;
  margin-top: 26px;
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 900px) {
  /* As many columns as there are cards, NOT a fixed three.
     A fixed three-column track left a brand-new deployment with one article
     showing a single third-width card against a full-bleed expanse of empty
     settlement -- which reads as a layout that failed, not as a board with
     one notice on it. The count comes from the fetch, so the renderer sets
     --lp-news-n; the fallback of 3 is what the markup is built around. */
  .lp-news-home-list {
    grid-template-columns: repeat(var(--lp-news-n, 3), minmax(0, 1fr));
  }
}
/* The cards in this row are shorter than the board's, so the summary is
   clamped rather than allowed to set three different card heights. */
.lp-news-home-list .lp-news-summary {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

/* ── One dispatch ───────────────────────────────────────────────────────────- */
.lp-article { max-width: 780px; }
/* The intro -- the same summary the card carries, set larger than the body so
   the reader gets the gist before committing to the article. */
.lp-article-intro {
  margin: 14px 0 0;
  max-width: 58ch;
  font-family: var(--font-note);
  font-size: clamp(16px, 1.9vw, 18.5px);
  line-height: 1.66;
  /* --bone, not --bone-dim, plus the headline's own shadow. An article sits
     directly on the settlement: .lp-subpage has no scrim, unlike .lp-blocks
     on the landing page, so this line is read over a lit campfire rather
     than over a dark panel. Dim grey was marginal there -- the title next to
     it already carries exactly this treatment for the same reason. */
  color: var(--bone);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.75);
}
.lp-article-intro[hidden], .lp-article-figure[hidden] { display: none; }

/* The header image. Framed like the paper below it so the two read as one
   document, and capped in height so a portrait upload cannot push the whole
   article below the fold. */
.lp-article-figure {
  margin: 22px 0 0;
  overflow: hidden;
  background: rgba(40, 31, 20, 0.35);
  border: 1px solid #8d7c57;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.55);
}
.lp-article-figure img {
  display: block;
  width: 100%;
  max-height: 460px;
  object-fit: cover;
  filter: sepia(0.24) saturate(0.9) contrast(1.03);
}
.lp-article-body {
  margin-top: 22px;
  padding: clamp(20px, 3vw, 36px);
  font-family: var(--font-note);
  font-size: 15.5px;
  line-height: 1.78;
  color: #2a2118;
  background:
    radial-gradient(90% 60% at 20% 0%, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 60%),
    linear-gradient(172deg, var(--paper) 0%, #c3b189 100%);
  border: 1px solid #8d7c57;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}

.lp-sheet-error { color: #6d2016; font-family: var(--font-technical); }

/* Shared prose styling for the two paper surfaces: the policy sheet and a
   news article. Both are built by markdown-lite.ts, so both produce exactly
   this small set of elements. */
.lp-sheet-body > *, .lp-article-body > * { margin: 0 0 1.05em; }
.lp-sheet-body > *:last-child, .lp-article-body > *:last-child { margin-bottom: 0; }
.lp-sheet-body h2, .lp-sheet-body h3, .lp-sheet-body h4,
.lp-article-body h2, .lp-article-body h3, .lp-article-body h4 {
  font-family: var(--font-display);
  font-weight: 800;
  line-height: 1.16;
  letter-spacing: 0.01em;
  color: #1d1610;
  margin-top: 1.7em;
}
.lp-sheet-body > h2:first-child, .lp-sheet-body > h3:first-child,
.lp-article-body > h2:first-child, .lp-article-body > h3:first-child { margin-top: 0; }
.lp-sheet-body h2, .lp-article-body h2 { font-size: 24px; }
.lp-sheet-body h3, .lp-article-body h3 { font-size: 19px; }
.lp-sheet-body h4, .lp-article-body h4 { font-size: 16px; letter-spacing: 0.06em; text-transform: uppercase; }
.lp-sheet-body strong, .lp-article-body strong { font-weight: 700; color: #1d1610; }
.lp-sheet-body em, .lp-article-body em { font-style: italic; color: #4a3d2c; }
/* Tailwind's preflight strips list markers from every ul/ol on the page, so a
   rendered policy came out as indented text with nothing marking the items.
   Put back explicitly, as squares — a disc reads as a web bullet and these two
   surfaces are paper. */
.lp-sheet-body ul, .lp-article-body ul { padding-left: 1.4em; list-style: square outside; }
.lp-sheet-body ol, .lp-article-body ol { padding-left: 1.5em; list-style: decimal outside; }
.lp-sheet-body li::marker, .lp-article-body li::marker { color: #8a6a33; }
.lp-sheet-body li, .lp-article-body li { margin-bottom: 0.4em; }
.lp-sheet-body blockquote, .lp-article-body blockquote {
  padding: 2px 0 2px 16px;
  border-left: 3px solid rgba(60, 48, 31, 0.5);
  color: #4a3d2c;
}
.lp-sheet-body hr, .lp-article-body hr {
  border: 0;
  border-top: 1px dashed rgba(60, 48, 31, 0.55);
  margin: 1.6em 0;
}

/* ── Responsive: the header collapses to logo + mute + play + burger ─────── */
@media (max-width: 899px) {
  .lp-nav { display: none; }
  /* The nav is what pushes the actions to the right edge, and it is gone here.
     An adjacent-sibling rule would NOT do: display:none removes the box, not
     the element, so .lp-nav + .lp-header-actions still matches. */
  .lp-header-actions { margin-left: auto; }
  .lp-burger { display: inline-flex; }
  .lp-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
  .lp-stat { padding: 16px 8px 13px; }

  /* Janeks: *"on mobile - Make the hero vertically smaller, move the buttons
     and the logo higher, closer to header."*

     On a phone the desktop arrangement — a 100dvh section with the copy
     pinned to its BOTTOM edge — spends a whole screen on backdrop and puts
     the wordmark and the Play button at the very bottom of it, a thumb's
     length below a header that is already showing the same logo. Here the
     hero is sized by its content instead: no viewport floor, the copy at the
     top, and the only reserved space is the fixed header's own height plus a
     small gap, so the logo starts immediately under the bar. The scene, the
     veils and the diagonal all still work — they are absolutely positioned
     against whatever height the copy produces. */
  .lp-hero { min-height: 0; align-items: flex-start; }
  .lp-hero-copy {
    /* The header is position:fixed, so it covers rather than displaces. */
    padding-top: calc(var(--lp-header-h) + 12px);
    padding-bottom: calc(clamp(26px, 4.5vh, 44px) + var(--lp-slant));
  }
  .lp-motto { margin-bottom: 12px; }
  .lp-pitch { margin: 14px 0 22px; }
}
@media (max-width: 560px) {
  .lp-header { gap: 7px; padding: 0 11px; }
  .lp-header-actions { gap: 6px; }
  /* The logo image is the whole wordmark now — it can't be trimmed down to a
     mark-only crop the way separate SVG+text could, but at 34px tall it is
     already narrower than the old text ever was, so it stays. */
  .lp-brand-logo { height: 28px; }
  .lp-label-wide { display: none; }
  .lp-label-narrow { display: inline; }
  .lp-btn { padding: 10px 13px; font-size: 13px; letter-spacing: 0.1em; gap: 6px; }
  .lp-icon-btn { width: 38px; padding: 0; }
  .lp-hero-actions .lp-btn { flex: 1 1 100%; }
  .lp-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
  .lp-stat { padding: 13px 4px 11px; }
  .lp-stat-label { letter-spacing: 0.12em; font-size: 9px; }
}

/* ── Leaving for the login form ──────────────────────────────────────────────
   Janeks: *"the webpage body moves to the left and fades."* The page slides
   out while the backdrop's camera pans right, so the two read as one move —
   the site getting out of the way of a shot that was always there.

   The duration is duplicated in auth-transition.ts as LEAVE_MS and the two
   must agree: that module navigates when this transition is over, and a
   mismatch either cuts the slide short or leaves the visitor looking at a
   faded page. A transitionend listener would avoid the duplication and
   introduce a worse failure — an interrupted transition never fires it, and
   the navigation would never happen. */
.lp-leaving {
  transition: transform 620ms cubic-bezier(0.45, 0, 0.2, 1), opacity 480ms ease-in;
  transform: translateX(-7vw);
  opacity: 0;
  pointer-events: none;
}

/* ── Arriving ────────────────────────────────────────────────────────────────
   Janeks: *"we have the animation of main site part disappearing, but not the
   appearing."* The page left toward -7vw, so it comes back FROM -7vw — the
   same move played backwards, which is what makes the return read as the
   camera bringing the site back rather than as a cut.

   A keyframe animation rather than a transition, deliberately: a transition
   needs a starting style, a forced reflow and a second frame to animate away
   from it, and the page it has to do all that on is the one mounting. An
   animation plays once, on its own, from the moment the class is present.

   backwards on the fill matters — it applies the 0% frame during the
   animation's delay, so the page cannot paint one frame at its final position
   before the move starts. */
.lp-entering {
  animation: lp-enter 560ms cubic-bezier(0.22, 0.7, 0.3, 1) backwards;
}
@keyframes lp-enter {
  from { transform: translateX(-7vw); opacity: 0; }
  to { transform: none; opacity: 1; }
}

/* ── The soft right edge ─────────────────────────────────────────────────────
   Janeks: *"Need to add some blur or gradient on the right side of the web
   containers, so that there is no sharp edge there."*

   Only while a live backdrop is up: without one there is no scene for the page
   to have an edge against, and the fade would just be a dark smear over a dark
   background. It is a mask rather than an overlay so the copy itself fades —
   an overlay would tint the text and leave its shape visible through the
   tint. */
@media (min-width: 1000px) {
  body.lp-backdrop-live .lp-section,
  body.lp-backdrop-live .lp-cta {
    -webkit-mask-image: linear-gradient(90deg, #000 0%, #000 62%, rgba(0, 0, 0, 0.55) 86%, rgba(0, 0, 0, 0) 100%);
    mask-image: linear-gradient(90deg, #000 0%, #000 62%, rgba(0, 0, 0, 0.55) 86%, rgba(0, 0, 0, 0) 100%);
  }
  /* A section holding a 3D preview is exempt: the preview lives on the right
     of it, and masking that column fades the model out along with the page. */
  body.lp-backdrop-live .lp-has-preview,
  body.lp-backdrop-live .lp-has-preview-weapon {
    -webkit-mask-image: none;
    mask-image: none;
  }
}

/* ── Motion ──────────────────────────────────────────────────────────────── */
/* Sections rise in as they scroll into view. Purely decorative, so the whole
   effect is skipped rather than shortened when motion is not wanted. */
.lp-reveal { opacity: 0; transform: translateY(22px); transition: opacity 0.6s ease, transform 0.6s ease; }
.lp-reveal[data-shown='true'] { opacity: 1; transform: none; }
@media (prefers-reduced-motion: reduce) {
  .lp-entering { animation: none; }
  .lp-reveal { opacity: 1; transform: none; transition: none; }
  .lp-btn:hover,
  .lp-points li:hover,
  .lp-news-card:hover { transform: none; }
  .lp-sheet { animation: none; }
  html:has(.lp) { scroll-behavior: auto; }
}
`;

/** Injects the sheet if it is not already there; returns a remover. */
export function injectLandingStyles(): () => void {
  const existing = document.getElementById(STYLE_ID);
  if (existing) return () => { /* owned by an earlier mount */ };
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
  return () => style.remove();
}
